import React, { Fragment, useState, useEffect, useRef } from "react";
import { useOutletContext, useLocation} from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import { ActiveConstraintsBar } from "../../components/ui-components/ActiveConstraintsBar.jsx";
import { PieChartComponent } from "../../components/chart-components/PieChartComponent.jsx";
import { BarChartComponent } from "../../components/BarChartComponent.js";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";


const API_URL = process.env.REACT_APP_API_URL;

const OrganizationL5_Overview = () => {
    const [filters, setFilters] = useState([]);
    const [costTrend, ] = useState([]);
    const [totalCost, setTotalCost] = useState(0);
    const [costPerProject, setCostPerProject] = useState([]);
    const [costPerOrganisation, setCostPerOrganisation] = useState([]);
    const [projectCostPerMonth, setProjectCostPerMonth] = useState(0);
    const [organisationCostPerMonth, setOrganisationCostPerMonth] = useState(0);
    const [costComponentCostPerMonth, setCostComponentCostPerMonth] = useState(0);
    const [costPerCostComponentGroup, setCostPerCostComponentGroup] = useState(0);
	const { keycloak } = useKeycloak();
    // Use Zustand store for date state with moment dates
    const { startDate,  endDate } = useFilterStoreWithMomentDates();
    const { jwt } = useOutletContext();
    const location = useLocation();

    // initialization for the variables for the fuzzy search, the filters and the grouping
	const filtersRef = useRef();
	filtersRef.current = filters;

    function retrieveCostPerProject() {
        if (jwt === "") return;

        const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqMeasures.push({name: "cost", formula: "sum(direct_cost)"});
		reqGrouping.push("project_name");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: false,
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost-aggregated", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
                setCostPerProject(json.CloudCost.details);
			});
    }

    function retrieveCostPerOrganisation() {
        if (jwt === "") return;

        const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];
		let reqJoinFilters = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

        reqJoinFilters.push({
			field: "level",
			operator: "=",
			values: ["1"],
		});

		reqMeasures.push({name: "cost", formula: "sum(indirect_cost + indirect_allocated_cost)"});
		reqGrouping.push("organisation_name");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: false,
			join_filters: reqJoinFilters,
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost-aggregated", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
                setCostPerOrganisation(json.CloudCost.details);
			});
    }

    function retrieveCostPerCostComponentGroup() {
        if (jwt === "") return;

        const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqMeasures.push({name: "cost", formula: "sum(direct_cost)"});
		reqGrouping.push("cost_resource_type");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: false,
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost-aggregated", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
                setCostPerCostComponentGroup(json.CloudCost.details);
			});
    }

    function calcTotalCost(grouped_totals) {
		let currentPeriod = 0;
		let previousPeriod = 0;

		if (grouped_totals !== null && grouped_totals.length > 0) {
			grouped_totals.forEach((group) => {
				currentPeriod += group.current_period;
				previousPeriod += group.previous_period;
			}); 
		}

		setTotalCost({
			currentValue: currentPeriod,
			previousValue: previousPeriod,
		});
	}

    function retrieveCostPerProjectPerMonth() {
		if (jwt === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqMeasures.push({
			name: "measure",
			formula: "sum(field.direct_cost)",
		});

		reqGrouping.push("project_name");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: true,
			add_grouped_results: true,
            date_granularity: "month",
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
				if (json.CloudCost && json.CloudCost.details) {
					json.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setProjectCostPerMonth(json.CloudCost);
					calcTotalCost(json.CloudCost.grouped_totals);
				}
				else {
					setProjectCostPerMonth([]);
					calcTotalCost([]);
				}
			});
	}

    function retrieveCostPerOrganisationPerMonth() {
		if (jwt === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];
		let reqJoinFilters = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

        reqJoinFilters.push({
			field: "level",
			operator: "=",
			values: ["1"],
		});

		reqMeasures.push({
			name: "measure",
			formula: "sum(field.indirect_cost + indirect_allocated_cost)",
		});

		reqGrouping.push("organisation_name");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: true,
			add_grouped_results: true,
            date_granularity: "month",
			join_filters: reqJoinFilters,
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
				if (json.CloudCost && json.CloudCost.details) {
					json.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setOrganisationCostPerMonth(json.CloudCost);
				}
				else {
					setOrganisationCostPerMonth([]);
				}
			});
	}

    function retrieveCostPerCostComponentGroupPerMonth() {
		if (jwt === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)

		let method = "POST";

        let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqMeasures.push({
			name: "measure",
			formula: "sum(field.direct_cost)",
		});

		reqGrouping.push("cost_resource_type");

		const requestBody = {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: true,
			add_grouped_results: true,
            date_granularity: "month",
		};

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		fetch(API_URL + "/get-cloud-cost", requestOptions)
			.then((response) => {
				return response.json();
			})
			.then((json) => {
				if (json.CloudCost && json.CloudCost.details) {
					json.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setCostComponentCostPerMonth(json.CloudCost);
				}
				else {
					setCostComponentCostPerMonth([]);
				}
			});
	}

    useEffect(() => {
		retrieveCostPerProject();
        retrieveCostPerOrganisation();
        retrieveCostPerProjectPerMonth();
        retrieveCostPerOrganisationPerMonth();
        retrieveCostPerCostComponentGroupPerMonth();
        retrieveCostPerCostComponentGroup();
	// The fetchers are plain function declarations, so they are re-created on every
	// render; the deps below are the inputs that should actually trigger a refetch.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jwt, location, startDate, endDate, filters]);

    return (
        <Fragment>
            <AdministrationHeader
				title="Organisational Overview"
				className="text-text-3"
				icon={icon({name: "folder-tree", style: "light"})}
			/>
			<ActiveConstraintsBar
				token={keycloak.token}
				filters={filters}
				setFilters={setFilters}
				excludeCategories={[
					"organisation l1",
					"organisation l2",
					"organisation l3",
					"organisation l4",
					"organisation l6",
					"organisation l7",
					"organisation l8",
					"organisation l9",
					"organisation l10",
				]}
			/>

            <div className="text-left flex flex-auto flex-col ml-10 border-solid border-border">
                <div className="flex flex-row">
                    <KPI
                        title="Total Costs"
                        currentValue={totalCost}
                        trendData={costTrend}
                        format="currency"
                        showChange={false}
                    />
                </div>

                <div className=" py-2 my-2 rounded-lg h-screen w-screen flex flex-auto flex-col">
                    {/* <div className="text-coolgrey3 text-xl font-semibold mt-4 mb-2">
                        Cost per Project
                    </div> */}
                    <div className=" rounded-lg h-[260px] w-full flex flex-row">
                        <div className="w-[33%]">
                             <PieChartComponent
                                data={costPerOrganisation}
                                value="cost"
                                name="organisation_name"
                                title="Organisation"
                            />
                        </div>
                        <div className="w-[33%]">
                            <PieChartComponent
                                data={costPerProject}
                                value="cost"
                                name="project_name"
                                title="Account"
                            />
                        </div>
                        <div className="w-[33%]">
                            <PieChartComponent
                                data={costPerCostComponentGroup}
                                value="cost"
                                name="cost_resource_type"
                                title="Cost resource cluster"
                            />
                        </div>
                    </div>

                    <div className=" rounded-lg mt-12 h-[260px] w-full flex flex-row">
                        <div className="w-[33%]">
                            <BarChartComponent
                                data={organisationCostPerMonth.details}
                                dateFormat="MMM YYYY"
                                title="Organisation per Month"
                                valueName="Total Costs"
                                legendPosition="right"
                            />
                        </div>
                        <div className="w-[33%]">
                            <BarChartComponent
                                data={projectCostPerMonth.details}
                                dateFormat="MMM YYYY"
                                title="Account per Month"
                                valueName="Total Costs"
                                legendPosition="right"
                            />
                        </div>
                        <div className="w-[33%]">
                        <BarChartComponent
                                data={costComponentCostPerMonth.details}
                                dateFormat="MMM YYYY"
                                title="Cost resource cluster per Month"
                                valueName="Total Costs"
                                legendPosition="right"
                            />
                        </div>
                    </div>

                </div>
            </div>
        </Fragment>
    );
};

export default OrganizationL5_Overview;

